/* =========================================
   WING LOAD CALCULATOR V2
   Phase 1: E-Tier fix, Velocity name fix,
            input clamping, div-by-zero guard.
   Phase 4: IIFE, addEventListener, cached DOM,
            CSS class toggles, clean state sync.
   Phase 6: URL state sharing, tandem canopies.
   ========================================= */

(function () {
    'use strict';

    /* ---- CONSTANTS ---- */
    var KG_TO_LBS = 2.20462;
    var FT_TO_M = 0.3048;

    /* ---- STATE ---- */
    var state = {
        isMetric: true,
        isWS: false,
        isTandem: false,
        isReserve: false,
        selectedModel: null,
        selectedVariantId: null,
        currentProduct: null
    };

    /* ---- CACHED DOM REFS (populated in init) ---- */
    var dom = {};

    /* ---- UTILITY ---- */
    function clamp(val, min, max) {
        return Math.max(min, Math.min(max, val));
    }

    /* ================================================
       TIER DATA (Phase 1: fixed E-Tier + Velocity)
       ================================================ */
    var tiers = {
        F: {
            name: "F-Tier", limit: 1.0,
            desc: "Student & Accuracy. Large 7/9-cells. Focus on safety and forgiveness.",
            models: ["Navigator", "Equinox", "Prime", "Karma"]
        },
        E: {
            name: "E-Tier", limit: 1.3,
            desc: "7-Cell / Wingsuit / Tracking. Docile openings, soft on-heading reliability.",
            models: ["Spectre", "Storm", "Horizon", "Om", "Pilot7", "Triathlon", "Echo", "Helium", "Epicene Pro 2", "Omicron"]
        },
        D: {
            name: "D-Tier", limit: 1.5,
            desc: "Intermediate/Sport 9-Cell. Great glide, flare, and daily fun.",
            models: ["Sabre2", "Sabre3", "Pulse", "Safire 3", "S-Fire", "Pilot", "Silhouette", "Nexus", "Sky"]
        },
        C: {
            name: "C-Tier", limit: 1.8,
            desc: "Advanced Elliptical. Faster turns, longer recovery arc.",
            models: ["Katana", "Crossfire 3", "Gangster", "Zulu", "Mamba", "Diablo", "XFire", "Stiletto"]
        },
        B: {
            name: "B-Tier", limit: 2.2,
            desc: "Cross-Braced Entry. High-performance swooping introduction.",
            models: ["Velocity / Comp Velocity", "JFX 2", "GT-R", "Airwolf2"]
        },
        A: {
            name: "A-Tier", limit: 2.6,
            desc: "Hyper Performance. Highly elliptical, advanced sail materials.",
            models: ["Valkyrie", "Leia", "JVX", "SAW"]
        },
        S: {
            name: "S-Tier", limit: 3.5,
            desc: "Competition Class. Pure speed, expert use only.",
            models: ["Petra", "Peregrine", "HK2 / HK3 / HK Terminal", "Sophia"]
        },
        T: {
            name: "Tandem", limit: 1.1,
            desc: "Tandem-rated canopies. Large docile canopies designed for dual-harness jumps.",
            models: ["Sigma", "Icarus Tandem", "Navigator Tandem"]
        },
        R: {
            name: "Reserve", limit: 1.4,
            desc: "Reserve canopies. LPV models pack one size smaller. Standard models use F-111 fabric.",
            models: ["PD Optimum", "Smart LPV", "Nano", "PD Reserve", "Smart Reserve", "Icarus Reserve"]
        }
    };

    /* ================================================
       CANOPY SIZE DATABASE
       Phase 1: Velocity key matches tier name exactly.
       Phase 6: Tandem sizes added.
       ================================================ */
    var canopySpecs = {
        // PERFORMANCE DESIGNS
        "Sabre2": [97, 107, 120, 135, 150, 170, 190, 210, 230, 260],
        "Sabre3": [89, 97, 107, 120, 135, 150, 170, 190, 210, 230],
        "Pulse": [107, 120, 135, 150, 170, 190, 210, 230, 260],
        "Storm": [97, 107, 120, 135, 150, 170, 190, 210, 230],
        "Spectre": [97, 107, 120, 135, 150, 170, 190, 210, 230],
        "Horizon": [120, 135, 150, 170, 190],
        "Navigator": [200, 220, 240, 260, 280, 300],
        "Silhouette": [135, 150, 170, 190, 210, 230, 260],
        "Stiletto": [89, 97, 107, 120, 135, 150, 170, 190],
        "Katana": [83, 89, 97, 107, 120, 135, 150, 170],
        "Velocity / Comp Velocity": [75, 79, 84, 90, 96, 103, 111, 120],
        "Valkyrie": [67, 71, 75, 79, 84, 90, 96, 103],
        "Peregrine": [61, 64, 67, 69, 71, 75, 79, 84, 90],
        "Sigma": [340, 365],

        // FLUID WINGS
        "Prime": [110, 120, 130, 140, 150, 160, 170, 190],
        "Nexus": [120, 135, 150, 170, 190],
        "Echo": [97, 107, 120, 135, 150, 170],
        "Gangster": [79, 84, 90, 96, 107, 120, 135],
        "Airwolf2": [67, 71, 75, 79, 84, 90, 96, 103],
        "HK2 / HK3 / HK Terminal": [60, 64, 67, 69, 71, 75, 79, 84],
        "SAW": [67, 71, 75, 79, 84, 89],

        // NZ AEROSPORTS / JYRO
        "Leia": [59, 64, 69, 72, 75, 79, 84, 89, 94, 99],
        "JFX 2": [69, 74, 79, 84, 89, 94, 99, 104, 109, 119],
        "Crossfire 3": [89, 99, 109, 119, 129, 139, 149, 159, 169],
        "Safire 3": [99, 109, 119, 129, 139, 149, 169, 189, 209, 229],
        "Petra": [55, 59, 64, 69, 72, 75, 79, 84, 89],
        "Helium": [129, 139, 149, 169, 189, 209, 229],
        "JVX": [66, 69, 72, 75, 79, 84, 89, 94, 99, 104, 109, 119],

        // ICARUS WORLD
        "XFire": [75, 83, 92, 102, 113, 124, 138, 153, 168],
        "GT-R": [67, 71, 75, 79, 84, 90, 96, 103],
        "S-Fire": [99, 109, 119, 129, 139, 149, 169, 189, 209, 229],
        "Om": [99, 109, 119, 129, 139, 149, 169, 189, 209, 229],
        "Equinox": [189, 199, 209, 219, 229, 239, 249, 259, 279, 289, 299],
        "Sky": [149, 169, 189, 209],
        "Sophia": [60, 65, 70, 75, 80],
        "Icarus Tandem": [330, 365, 400],

        // AERODYNE
        "Pilot": [90, 96, 104, 111, 117, 124, 132, 140, 150, 168, 188, 210, 230, 250],
        "Pilot7": [117, 127, 137, 147, 167, 187, 207, 227, 247, 277],
        "Karma": [93, 99, 109, 119, 129, 149],
        "Zulu": [92, 102, 112, 122, 132, 152, 172],
        "Triathlon": [99, 120, 135, 150, 160, 175, 190, 210, 220, 240, 260],
        "Mamba": [90, 96, 104, 111, 117, 124, 132, 140, 150],
        "Diablo": [88, 99, 110, 120, 135, 150, 170, 190],
        "Navigator Tandem": [360, 400],

        // SQUIRREL
        "Epicene Pro 2": [116, 130, 150, 170, 190, 210],
        "Omicron": [125, 130, 140, 150, 160, 170, 180, 190, 200, 210, 220, 230],

        // RESERVES - LPV
        "PD Optimum": [99, 106, 113, 126, 143, 160, 176, 193, 218, 235, 253],
        "Smart LPV": [99, 110, 120, 135, 150, 160, 175, 190, 220, 250],
        "Nano": [99, 106, 113, 126, 143, 160, 176, 193, 218, 235, 253],

        // RESERVES - STANDARD
        "PD Reserve": [99, 106, 113, 126, 143, 160, 176, 193, 218, 235, 253, 281],
        "Smart Reserve": [99, 110, 120, 135, 150, 160, 175, 190, 220, 250],
        "Icarus Reserve": [99, 109, 119, 129, 139, 149, 169, 189, 219, 239, 259, 279]
    };

    /* ================================================
       MAX WING LOAD BY EXPERIENCE
       ================================================ */
    function getMaxWLForJumps(jumps, isWS) {
        if (isWS) return 1.4;
        if (jumps <= 50)   return 1.0;  // Student
        if (jumps <= 100)  return 1.1;  // Fresh B-License
        if (jumps <= 150)  return 1.2;  // B-License
        if (jumps <= 200)  return 1.3;  // Weekend Warrior
        if (jumps <= 300)  return 1.4;  // C-License
        if (jumps <= 400)  return 1.5;  // C-License+
        if (jumps <= 500)  return 1.6;  // Intermediate
        if (jumps <= 650)  return 1.7;  // Intermediate+
        if (jumps <= 950)  return 1.8;  // Advanced
        if (jumps <= 1200) return 2.0;  // Advanced+
        if (jumps <= 1500) return 2.2;  // Expert
        if (jumps <= 1800) return 2.4;  // Pro
        if (jumps <= 2100) return 2.6;  // Pro+
        if (jumps <= 2500) return 2.8;  // Competition Entry
        return 3.0;                     // Competition
    }

    /* ================================================
       ADJUSTED MAX WL (courses + altitude)
       ================================================ */
    function getAdjustedMaxWL(jumps, isWS) {
        var base = getMaxWLForJumps(jumps, isWS);

        // Canopy courses: +0.1 per course beyond the 1st
        var courses = parseInt(dom.inCourses ? dom.inCourses.value : 1, 10) || 1;
        var courseBonus = Math.max(0, (courses - 1)) * 0.1;

        // DZ altitude: -0.04 per 1000 ft (convert from meters if metric)
        var altitude = parseFloat(dom.inAltitude ? dom.inAltitude.value : 0) || 0;
        var altFt = state.isMetric ? altitude / FT_TO_M : altitude;
        var altPenalty = (altFt / 1000) * 0.04;

        var result = Math.round((base + courseBonus - altPenalty) * 100) / 100;
        if (state.isReserve) return Math.min(result, 1.4);
        return result;
    }

    /* ================================================
       READ / WRITE URL STATE  (Phase 6)
       ================================================ */
    function readURLState() {
        var params = new URLSearchParams(window.location.search);
        if (!params.has('wlc')) return; // only act if our flag is present

        var jumps = parseFloat(params.get('jumps'));
        var weight = parseFloat(params.get('weight'));
        var gear = parseFloat(params.get('gear'));
        var size = parseFloat(params.get('size'));
        var unit = params.get('unit');
        var ws = params.get('ws');
        var tandem = params.get('tandem');

        if (unit === 'lbs' && state.isMetric) {
            state.isMetric = false;
            dom.unitPill.classList.remove('active-metric');
            dom.unitPill.classList.add('active-imperial');
            document.querySelectorAll('.unit-display').forEach(function (l) { l.innerText = 'LBS'; });
            updateRangeLimits(false);
        }

        if (ws === '1') {
            state.isWS = true;
            dom.btnWS.classList.add('active');
            dom.btnWS.setAttribute('aria-pressed', 'true');
        }
        if (tandem === '1') {
            state.isTandem = true;
            dom.btnTandem.classList.add('active');
            dom.btnTandem.setAttribute('aria-pressed', 'true');
            updateRangeLimits(state.isMetric);
        }
        var reserve = params.get('reserve');
        if (reserve === '1') {
            state.isReserve = true;
            dom.btnReserve.classList.add('active');
            dom.btnReserve.setAttribute('aria-pressed', 'true');
        }

        if (!isNaN(jumps)) { dom.inExp.value = jumps; dom.slExp.value = clamp(jumps, 0, 2000); }
        if (!isNaN(weight)) { dom.inWeight.value = weight; dom.slWeight.value = weight; }
        if (!isNaN(gear)) { dom.inGear.value = gear; dom.slGear.value = gear; }
        if (!isNaN(size)) { dom.inSize.value = size; dom.slSize.value = size; }

        var courses = parseInt(params.get('courses'), 10);
        var altitude = parseFloat(params.get('alt'));
        if (!isNaN(courses) && courses > 1) {
            dom.inCourses.value = courses; dom.slCourses.value = courses;
            dom.advPanel.style.display = 'block';
            dom.advToggle.setAttribute('aria-expanded', 'true');
        }
        if (!isNaN(altitude) && altitude > 0) {
            dom.inAltitude.value = altitude; dom.slAltitude.value = altitude;
            dom.advPanel.style.display = 'block';
            dom.advToggle.setAttribute('aria-expanded', 'true');
        }
    }

    function writeURLState() {
        if (!window.history || !window.history.replaceState) return;
        var params = new URLSearchParams(window.location.search);
        params.set('wlc', '1');
        params.set('jumps', dom.inExp.value);
        params.set('weight', dom.inWeight.value);
        params.set('gear', dom.inGear.value);
        params.set('size', dom.inSize.value);
        params.set('unit', state.isMetric ? 'kg' : 'lbs');
        params.set('ws', state.isWS ? '1' : '0');
        params.set('tandem', state.isTandem ? '1' : '0');
        params.set('reserve', state.isReserve ? '1' : '0');
        params.set('courses', dom.inCourses.value);
        params.set('alt', dom.inAltitude.value);
        var newURL = window.location.pathname + '?' + params.toString();
        window.history.replaceState(null, '', newURL);
    }

    /* ================================================
       INPUT HANDLING  (Phase 1: clamping + guards)
       ================================================ */
    function updateFromInput(key) {
        var input = dom['in' + cap(key)];
        var slider = dom['sl' + cap(key)];
        var val = parseFloat(input.value);
        if (isNaN(val)) return;

        // Clamp to slider range
        var min = parseFloat(slider.min);
        var max = parseFloat(slider.max);
        val = clamp(val, min, max);
        input.value = val;
        slider.value = val;

        if (key === 'wl') {
            adjustSizeFromWL(val);
        } else {
            calculate(false);
        }
    }

    function updateFromSlider(key) {
        var slider = dom['sl' + cap(key)];
        var input = dom['in' + cap(key)];
        var val = parseFloat(slider.value);
        input.value = val;

        if (key === 'wl') {
            adjustSizeFromWL(val);
        } else {
            calculate(false);
        }
    }

    /* Capitalise first letter for DOM key lookup */
    function cap(s) {
        var map = { exp: 'Exp', weight: 'Weight', gear: 'Gear', size: 'Size', wl: 'WL' };
        return map[s] || s;
    }

    function adjustSizeFromWL(targetWL) {
        var w = parseFloat(dom.inWeight.value);
        var g = parseFloat(dom.inGear.value);
        var totalLbs = state.isMetric ? (w + g) * KG_TO_LBS : (w + g);

        if (targetWL <= 0) return;  // guard div-by-zero
        var newSize = Math.round(totalLbs / targetWL);
        var maxS = parseFloat(dom.slSize.max);
        var minS = parseFloat(dom.slSize.min);
        var clampedSize = clamp(newSize, minS, maxS);

        dom.inSize.value = clampedSize;
        dom.slSize.value = clampedSize;
        calculate(true);
    }

    /* ================================================
       CORE CALCULATION  (Phase 1: E-Tier fix)
       ================================================ */
    function calculate(skipWLUpdate) {
        var w = parseFloat(dom.inWeight.value) || 0;
        var g = parseFloat(dom.inGear.value) || 0;
        var size = parseFloat(dom.inSize.value) || 1; // guard div-by-zero
        var jumps = parseFloat(dom.inExp.value) || 0;

        if (size < 1) size = 1; // extra safety

        var totalLbs = state.isMetric ? (w + g) * KG_TO_LBS : (w + g);
        var totalKg = totalLbs / KG_TO_LBS;
        var wl = totalLbs / size;

        // Update WL input/slider unless driven by WL itself
        if (!skipWLUpdate) {
            dom.inWL.value = wl.toFixed(2);
            dom.slWL.value = wl.toFixed(2);
        }

        // Display
        dom.resWL.innerText = wl.toFixed(2);

        var weightStr;
        if (state.isMetric) {
            weightStr = Math.round(totalKg) + ' kg (' + Math.round(totalLbs) + ' lbs)';
        } else {
            weightStr = Math.round(totalLbs) + ' lbs (' + Math.round(totalKg) + ' kg)';
        }
        dom.resTotalW.innerText = weightStr;

        // Meter bar width
        var pct = Math.min((wl / 3.5) * 100, 100);
        dom.resBar.style.width = pct + '%';

        /* --- TIER MAPPING (Phase 1: E-Tier now reachable) --- */
        var recKey;
        if (state.isTandem) {
            recKey = 'T';
        } else if (state.isWS) {
            recKey = 'E';
        } else if (state.isReserve) {
            recKey = 'R';
        } else {
            if (wl < 1.05) recKey = 'F';
            else if (wl < 1.3) recKey = 'E';     // FIXED: was missing in v1
            else if (wl < 1.5) recKey = 'D';
            else if (wl < 1.8) recKey = 'C';
            else if (wl < 2.2) recKey = 'B';
            else if (wl < 2.6) recKey = 'A';
            else recKey = 'S';
        }

        var data = tiers[recKey];
        dom.resTierName.innerText = data.name;
        dom.resDesc.innerText = data.desc;

        // Render canopy model tags
        renderCanopyTags(data);

        // Re-render sizes if a model is selected
        if (state.selectedModel) {
            renderSizes(state.selectedModel);
        }

        // Safety alerts
        evaluateSafety(wl, jumps);

        // Persist to URL
        writeURLState();
    }

    /* ================================================
       SAFETY ALERTS  (cleaner, class-driven)
       ================================================ */
    function evaluateSafety(wl, jumps) {
        var alertBox = dom.resAlert;
        var badge = dom.resStatus;
        var bar = dom.resBar;

        // Reset
        alertBox.className = 'sd-alert';
        alertBox.innerHTML = '';
        badge.className = 'sd-tier-tag';
        bar.className = 'sd-meter-bar';

        var maxWL = getAdjustedMaxWL(jumps, state.isWS);

        if (wl > 1.05 && jumps <= 50) {
            alertBox.innerHTML = '<strong>STOP:</strong> Students must stay below 1.0 wing loading.';
            alertBox.classList.add('visible', 'sd-alert-danger');
            badge.innerText = 'UNSAFE';
            badge.classList.add('tag-unsafe');
            bar.classList.add('meter-danger');
        }
        else if (wl > maxWL) {
            alertBox.innerHTML = '<strong>Warning:</strong> Wing load ' + wl.toFixed(2) +
                ' exceeds the recommended limit (' + maxWL + ') for your experience level.';
            alertBox.classList.add('visible', 'sd-alert-danger');
            badge.innerText = 'Aggressive';
            badge.classList.add('tag-aggressive');
            bar.classList.add('meter-danger');
        }
        else if (state.isWS && wl > 1.4) {
            alertBox.innerHTML = '<strong>Caution:</strong> Wingsuiting > 1.4 WL increases malfunction risk and opening shock.';
            alertBox.classList.add('visible', 'sd-alert-warn');
            badge.innerText = 'Caution';
            badge.classList.add('tag-caution');
            bar.classList.add('meter-caution');
        }
        else {
            badge.innerText = 'Optimal';
            badge.classList.add('tag-optimal');
            bar.classList.add('meter-safe');
        }
    }

    /* ================================================
       CANOPY TAGS
       ================================================ */
    function renderCanopyTags(data) {
        var container = dom.resCanopies;
        container.innerHTML = '';

        data.models.forEach(function (m) {
            var t = document.createElement('span');
            t.className = 'sd-tag';
            t.innerText = m;
            t.dataset.model = m;
            t.setAttribute('role', 'button');
            t.setAttribute('tabindex', '0');
            t.setAttribute('aria-label', 'Select ' + m + ' canopy');
            if (state.selectedModel === m) t.classList.add('selected');
            container.appendChild(t);
        });
    }

    /* ================================================
       BEST SIZE FINDER
       ================================================ */
    function findBestSize(sizes, totalLbs, maxWL) {
        var sweetSpot = state.isReserve ? 0.85 : 0.9;
        var targetWL = maxWL * sweetSpot; // reserve: prefer one size bigger
        var bestSize = null;
        var bestDiff = Infinity;
        sizes.forEach(function (s) {
            var wl = s > 0 ? totalLbs / s : 99;
            if (wl <= maxWL) {
                var diff = Math.abs(wl - targetWL);
                if (diff < bestDiff) {
                    bestDiff = diff;
                    bestSize = s;
                }
            }
        });
        return bestSize;
    }

    /* ================================================
       SIZE GRID
       ================================================ */
    function renderSizes(modelName) {
        var sizeArea = dom.resSizesArea;
        var sizeTitle = dom.selectedModelName;
        var sizeGrid = dom.sizeButtons;

        sizeArea.style.display = 'block';
        sizeTitle.innerText = modelName;
        sizeGrid.innerHTML = '';

        var sizes = canopySpecs[modelName] || [120, 135, 150, 170, 190, 210];

        var w = parseFloat(dom.inWeight.value) || 0;
        var g = parseFloat(dom.inGear.value) || 0;
        var totalLbs = state.isMetric ? (w + g) * KG_TO_LBS : (w + g);
        var jumps = parseFloat(dom.inExp.value) || 0;
        var maxWL = getAdjustedMaxWL(jumps, state.isWS);
        var currentSize = parseFloat(dom.inSize.value) || 0;
        var bestSize = findBestSize(sizes, totalLbs, maxWL);

        sizes.forEach(function (s) {
            var btnWL = s > 0 ? totalLbs / s : 99; // guard

            var btn = document.createElement('div');
            btn.className = 'sd-size-btn';
            btn.setAttribute('role', 'button');
            btn.setAttribute('tabindex', '0');
            btn.setAttribute('aria-label', s + ' sq ft, wing load ' + btnWL.toFixed(2) + (s === bestSize ? ', best fit for you' : ''));

            if (Math.abs(currentSize - s) < 2) btn.classList.add('active-size');
            btn.classList.add(btnWL <= maxWL + 0.05 ? 'size-safe' : 'size-unsafe');
            if (s === bestSize) btn.classList.add('best-fit');

            var label = '<span class="sd-size-val">sq ft ' + s + '</span><span class="sd-size-wl">WL ' + btnWL.toFixed(2) + '</span>';
            if (s === bestSize) label += '<span class="sd-best-label">Best Fit</span>';
            btn.innerHTML = label;
            btn.dataset.size = s;
            sizeGrid.appendChild(btn);
        });
    }

    function selectSize(size) {
        dom.inSize.value = size;
        dom.slSize.value = size;
        calculate(false);
    }

    /* ================================================
       MODEL SELECTION + PRODUCT CARD
       ================================================ */
    function selectModel(modelName) {
        state.selectedModel = modelName;

        document.querySelectorAll('.sd-tag').forEach(function (t) {
            if (t.dataset.model === modelName) t.classList.add('selected');
            else t.classList.remove('selected');
        });

        renderSizes(modelName);
        showProductCard(modelName);
    }

    function findProductByModel(modelName) {
        if (!window.shopifyCanopyProducts || !Array.isArray(window.shopifyCanopyProducts)) {
            return null;
        }
        var searchName = modelName.toLowerCase().replace(/\s+/g, '');
        return window.shopifyCanopyProducts.find(function (product) {
            var title = product.title.toLowerCase().replace(/\s+/g, '');
            var handle = product.handle.toLowerCase();
            return title.includes(searchName) || handle.includes(searchName);
        });
    }

    function showProductCard(modelName) {
        var section = dom.productCardSection;
        var productData = findProductByModel(modelName);

        if (!productData) {
            section.style.display = 'none';
            state.selectedVariantId = null;
            state.currentProduct = null;
            return;
        }

        document.getElementById('product-card-image').src = productData.featured_image;
        document.getElementById('product-card-image').alt = productData.title;
        document.getElementById('product-card-title').textContent = productData.title;
        document.getElementById('product-card-price').textContent = productData.price_formatted;
        document.getElementById('product-card-link').href = productData.url;

        var badge = document.getElementById('product-card-badge');
        if (productData.available) {
            badge.textContent = 'Available';
            badge.classList.remove('unavailable');
        } else {
            badge.textContent = 'Out of Stock';
            badge.classList.add('unavailable');
        }

        renderProductCardSizes(modelName, productData);
        section.style.display = 'block';
        section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function renderProductCardSizes(modelName, productData) {
        var container = document.getElementById('product-card-sizes');
        container.innerHTML = '';
        state.selectedVariantId = null;
        state.currentProduct = productData;
        updateATCButton();

        var sizes = canopySpecs[modelName] || [];
        if (sizes.length === 0) {
            container.innerHTML = '<span style="color: #999; font-size: 12px;">Size information not available</span>';
            return;
        }

        var w = parseFloat(dom.inWeight.value) || 0;
        var g = parseFloat(dom.inGear.value) || 0;
        var jumps = parseFloat(dom.inExp.value) || 0;
        var totalLbs = state.isMetric ? (w + g) * KG_TO_LBS : (w + g);
        var maxWL = getAdjustedMaxWL(jumps, state.isWS);
        var bestSize = findBestSize(sizes, totalLbs, maxWL);

        sizes.forEach(function (size) {
            var variant = null;
            if (productData && productData.variants) {
                variant = productData.variants.find(function (v) {
                    var sizeStr = String(size);
                    return v.size === sizeStr || v.size === (sizeStr + ' sq ft') || v.title.includes(sizeStr);
                });
            }

            var sizeWL = size > 0 ? totalLbs / size : 99;

            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'sd-size-chip';
            if (variant && variant.available) chip.classList.add('available');
            else if (variant && !variant.available) chip.classList.add('unavailable');
            if (size === bestSize) chip.classList.add('best-fit');

            var chipLabel = size + ' sq ft <small>(WL: ' + sizeWL.toFixed(2) + ')</small>';
            if (size === bestSize) chipLabel += '<span class="sd-best-label">Best Fit</span>';
            chip.innerHTML = chipLabel;

            if (variant) {
                chip.dataset.variantId = variant.id;
                chip.dataset.size = size;
                chip.dataset.price = variant.price_formatted;
                chip.title = variant.available
                    ? 'Select ' + modelName + ' ' + size + ' sq ft - ' + variant.price_formatted
                    : modelName + ' ' + size + ' sq ft - Out of Stock';
            } else {
                chip.title = modelName + ' ' + size + ' sq ft';
                chip.disabled = true;
            }

            container.appendChild(chip);
        });
    }

    function selectVariant(variantId, chipEl) {
        state.selectedVariantId = variantId;

        // Highlight selected chip
        var chips = document.querySelectorAll('#product-card-sizes .sd-size-chip');
        chips.forEach(function (c) { c.classList.remove('selected-variant'); });
        if (chipEl) chipEl.classList.add('selected-variant');

        // Update price display
        if (state.currentProduct && state.currentProduct.variants) {
            var v = state.currentProduct.variants.find(function (vr) { return vr.id === parseInt(variantId, 10); });
            if (v) {
                document.getElementById('product-card-price').textContent = v.price_formatted;
            }
        }

        updateATCButton();
    }

    function updateATCButton() {
        var btn = dom.productCardATC;
        if (!btn) return;
        var textEl = btn.querySelector('.sd-atc-btn__text');

        if (state.selectedVariantId) {
            btn.disabled = false;
            textEl.textContent = 'Add to Cart';
            btn.classList.remove('loading', 'success', 'error');
        } else {
            btn.disabled = true;
            textEl.textContent = 'Select a size';
            btn.classList.remove('loading', 'success', 'error');
        }
    }

    function addToCart() {
        if (!state.selectedVariantId) return;

        var btn = dom.productCardATC;
        var textEl = btn.querySelector('.sd-atc-btn__text');
        btn.disabled = true;
        btn.classList.add('loading');
        textEl.textContent = 'Adding...';

        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(state.selectedVariantId, 10), quantity: 1 }] })
        })
        .then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
        })
        .then(function () {
            btn.classList.remove('loading');
            btn.classList.add('success');
            textEl.textContent = 'Added!';
            updateCartBubble();

            // Dispatch event for theme cart drawers
            document.dispatchEvent(new CustomEvent('cart:refresh'));

            setTimeout(function () {
                btn.classList.remove('success');
                textEl.textContent = 'Add to Cart';
                btn.disabled = false;
            }, 2000);
        })
        .catch(function () {
            btn.classList.remove('loading');
            btn.classList.add('error');
            textEl.textContent = 'Error - Try Again';
            setTimeout(function () {
                btn.classList.remove('error');
                textEl.textContent = 'Add to Cart';
                btn.disabled = false;
            }, 2500);
        });
    }

    function updateCartBubble() {
        fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
                var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden], .cart-count, [data-cart-count]');
                bubbles.forEach(function (b) { b.textContent = cart.item_count; });
            })
            .catch(function () { /* silent */ });
    }

    /* ================================================
       UNIT TOGGLE
       ================================================ */
    function toggleUnits() {
        state.isMetric = !state.isMetric;
        var labels = document.querySelectorAll('.unit-display');

        var altLabels = document.querySelectorAll('.alt-unit-display');
        if (state.isMetric) {
            dom.unitPill.classList.remove('active-imperial');
            dom.unitPill.classList.add('active-metric');
            labels.forEach(function (l) { l.innerText = 'KG'; });
            altLabels.forEach(function (l) { l.innerText = 'm'; });
            convertInputsToMetric();
        } else {
            dom.unitPill.classList.remove('active-metric');
            dom.unitPill.classList.add('active-imperial');
            labels.forEach(function (l) { l.innerText = 'LBS'; });
            altLabels.forEach(function (l) { l.innerText = 'ft'; });
            convertInputsToImperial();
        }
        calculate(false);
    }

    function convertInputsToMetric() {
        var w = parseFloat(dom.inWeight.value);
        var g = parseFloat(dom.inGear.value);
        var alt = parseFloat(dom.inAltitude.value);
        var newW = Math.round(w / KG_TO_LBS);
        var newG = Math.round(g / KG_TO_LBS);
        var newAlt = Math.round(alt * FT_TO_M);
        updateRangeLimits(true);
        dom.inWeight.value = newW;
        dom.slWeight.value = newW;
        dom.inGear.value = newG;
        dom.slGear.value = newG;
        dom.slAltitude.min = 0; dom.slAltitude.max = 3000; dom.slAltitude.step = 50;
        dom.inAltitude.step = 50; dom.inAltitude.max = 3000;
        dom.inAltitude.value = newAlt;
        dom.slAltitude.value = newAlt;
    }

    function convertInputsToImperial() {
        var w = parseFloat(dom.inWeight.value);
        var g = parseFloat(dom.inGear.value);
        var alt = parseFloat(dom.inAltitude.value);
        var newW = Math.round(w * KG_TO_LBS);
        var newG = Math.round(g * KG_TO_LBS);
        var newAlt = Math.round(alt / FT_TO_M);
        updateRangeLimits(false);
        dom.inWeight.value = newW;
        dom.slWeight.value = newW;
        dom.inGear.value = newG;
        dom.slGear.value = newG;
        dom.slAltitude.min = 0; dom.slAltitude.max = 10000; dom.slAltitude.step = 100;
        dom.inAltitude.step = 100; dom.inAltitude.max = 10000;
        dom.inAltitude.value = newAlt;
        dom.slAltitude.value = newAlt;
    }

    function updateRangeLimits(metric) {
        if (state.isTandem) {
            dom.slSize.min = 300; dom.slSize.max = 400;
            if (metric) {
                dom.slWeight.min = 100; dom.slWeight.max = 250;
                dom.slGear.min = 20; dom.slGear.max = 40;
            } else {
                dom.slWeight.min = 220; dom.slWeight.max = 550;
                dom.slGear.min = 45; dom.slGear.max = 90;
            }
        } else {
            dom.slSize.min = 60; dom.slSize.max = 320;
            if (metric) {
                dom.slWeight.min = 40; dom.slWeight.max = 130;
                dom.slGear.min = 5; dom.slGear.max = 30;
            } else {
                dom.slWeight.min = 90; dom.slWeight.max = 280;
                dom.slGear.min = 10; dom.slGear.max = 60;
            }
        }
    }

    /* ================================================
       MODE TOGGLES
       ================================================ */
    function clearMode(except) {
        if (except !== 'ws' && state.isWS) {
            state.isWS = false;
            dom.btnWS.classList.remove('active');
            dom.btnWS.setAttribute('aria-pressed', 'false');
        }
        if (except !== 'tandem' && state.isTandem) {
            state.isTandem = false;
            dom.btnTandem.classList.remove('active');
            dom.btnTandem.setAttribute('aria-pressed', 'false');
        }
        if (except !== 'reserve' && state.isReserve) {
            state.isReserve = false;
            dom.btnReserve.classList.remove('active');
            dom.btnReserve.setAttribute('aria-pressed', 'false');
        }
    }

    function toggleMode(mode) {
        if (mode === 'ws') {
            state.isWS = !state.isWS;
            dom.btnWS.classList.toggle('active');
            dom.btnWS.setAttribute('aria-pressed', String(state.isWS));
            if (state.isWS) clearMode('ws');
            if (!state.isTandem) applyTandemDefaults();
        } else if (mode === 'tandem') {
            state.isTandem = !state.isTandem;
            dom.btnTandem.classList.toggle('active');
            dom.btnTandem.setAttribute('aria-pressed', String(state.isTandem));
            if (state.isTandem) clearMode('tandem');
            applyTandemDefaults();
        } else if (mode === 'reserve') {
            state.isReserve = !state.isReserve;
            dom.btnReserve.classList.toggle('active');
            dom.btnReserve.setAttribute('aria-pressed', String(state.isReserve));
            if (state.isReserve) clearMode('reserve');
            if (state.isTandem) applyTandemDefaults(); // reset from tandem ranges
        }
        state.selectedModel = null;
        state.selectedVariantId = null;
        state.currentProduct = null;
        dom.resSizesArea.style.display = 'none';
        dom.productCardSection.style.display = 'none';
        calculate(false);
    }

    function applyTandemDefaults() {
        updateRangeLimits(state.isMetric);
        if (state.isTandem) {
            var defWeight = state.isMetric ? 160 : 350;
            var defGear = state.isMetric ? 25 : 55;
            var defSize = 330;
            dom.inWeight.value = defWeight; dom.slWeight.value = defWeight;
            dom.inGear.value = defGear; dom.slGear.value = defGear;
            dom.inSize.value = defSize; dom.slSize.value = defSize;
        } else {
            var defWeight2 = state.isMetric ? 75 : 170;
            var defGear2 = state.isMetric ? 12 : 25;
            var defSize2 = 170;
            dom.inWeight.value = defWeight2; dom.slWeight.value = defWeight2;
            dom.inGear.value = defGear2; dom.slGear.value = defGear2;
            dom.inSize.value = defSize2; dom.slSize.value = defSize2;
        }
    }

    /* ================================================
       SHARE RESULTS
       ================================================ */
    function buildShareMessage() {
        var wl = dom.resWL.innerText;
        var tier = dom.resTierName.innerText;
        var total = dom.resTotalW.innerText;
        var size = dom.inSize.value;
        var model = state.selectedModel ? ' | Canopy: ' + state.selectedModel : '';
        return 'Wing Load Calculator Results\n' +
            'Wing Load: ' + wl + ' lbs/sqft\n' +
            'Exit Weight: ' + total + '\n' +
            'Canopy Size: ' + size + ' sq ft\n' +
            'Tier: ' + tier + model;
    }

    function buildShareURL() {
        writeURLState(); // ensure URL params are current
        return window.location.href;
    }

    function toggleSharePopup() {
        var popup = dom.sharePopup;
        if (popup.style.display === 'none' || !popup.style.display) {
            popup.style.display = 'block';
        } else {
            popup.style.display = 'none';
        }
    }

    function handleShare(platform) {
        var msg = buildShareMessage();
        var url = buildShareURL();
        var fullText = msg + '\n\n' + url;

        switch (platform) {
            case 'whatsapp':
                window.open('https://wa.me/?text=' + encodeURIComponent(fullText), '_blank');
                break;
            case 'messenger':
                window.open('https://www.facebook.com/dialog/send?link=' + encodeURIComponent(url) + '&app_id=&redirect_uri=' + encodeURIComponent(url), '_blank');
                break;
            case 'email':
                window.location.href = 'mailto:?subject=' + encodeURIComponent('My Wing Load Results') + '&body=' + encodeURIComponent(fullText);
                break;
            case 'copy':
                copyToClipboard(fullText, function (ok) {
                    var btn = dom.sharePopup.querySelector('[data-platform="copy"]');
                    showCopyFeedback(btn, ok ? 'Copied!' : 'Failed');
                });
                break;
            case 'instagram':
                copyToClipboard(fullText, function (ok) {
                    var btn = dom.sharePopup.querySelector('[data-platform="instagram"]');
                    showCopyFeedback(btn, ok ? 'Copied! Paste in IG DM' : 'Failed');
                });
                break;
        }

        if (platform !== 'copy' && platform !== 'instagram') {
            toggleSharePopup();
        }
    }

    function handleExpertContact(platform) {
        var msg = buildShareMessage();
        var url = buildShareURL();
        var body = 'Hi Ernestas,\n\nI used the Wing Load Calculator and would like your advice on my canopy progression.\n\n' + msg + '\n\n' + url;

        if (platform === 'whatsapp') {
            window.open('https://wa.me/34644271547?text=' + encodeURIComponent(body), '_blank');
        } else if (platform === 'email') {
            window.location.href = 'mailto:info@skydiveshop.com?subject=' + encodeURIComponent('Canopy Advice — Wing Load Calculator') + '&body=' + encodeURIComponent(body);
        }
    }

    function copyToClipboard(text, cb) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(function () { cb(true); }).catch(function () { cb(false); });
        } else {
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            try {
                var ok = document.execCommand('copy');
                cb(ok);
            } catch (e) {
                cb(false);
            }
            document.body.removeChild(ta);
        }
    }

    function showCopyFeedback(btn, msg) {
        var orig = btn.innerHTML;
        var origText = btn.textContent.trim();
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> ' + msg;
        btn.classList.add('sd-share-copied');
        setTimeout(function () {
            btn.innerHTML = orig;
            btn.classList.remove('sd-share-copied');
        }, 2000);
    }

    /* ================================================
       EVENT BINDING  (Phase 4: no inline handlers)
       ================================================ */
    function bindEvents() {
        // Unit toggle (click + keyboard)
        dom.unitPill.addEventListener('click', toggleUnits);
        dom.unitPill.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleUnits(); }
        });

        // Mode buttons
        dom.btnWS.addEventListener('click', function () { toggleMode('ws'); });
        dom.btnWS.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMode('ws'); }
        });
        dom.btnTandem.addEventListener('click', function () { toggleMode('tandem'); });
        dom.btnTandem.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMode('tandem'); }
        });
        dom.btnReserve.addEventListener('click', function () { toggleMode('reserve'); });
        dom.btnReserve.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMode('reserve'); }
        });

        // Slider + input pairs
        var pairs = ['exp', 'weight', 'gear', 'size', 'wl'];
        pairs.forEach(function (key) {
            var input = dom['in' + cap(key)];
            var slider = dom['sl' + cap(key)];
            input.addEventListener('input', function () { updateFromInput(key); });
            slider.addEventListener('input', function () { updateFromSlider(key); });
        });

        // Canopy tags (event delegation)
        dom.resCanopies.addEventListener('click', function (e) {
            var tag = e.target.closest('.sd-tag');
            if (tag && tag.dataset.model) selectModel(tag.dataset.model);
        });
        dom.resCanopies.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                var tag = e.target.closest('.sd-tag');
                if (tag && tag.dataset.model) { e.preventDefault(); selectModel(tag.dataset.model); }
            }
        });

        // Advanced settings toggle
        dom.advToggle.addEventListener('click', function () {
            var open = dom.advPanel.style.display !== 'none';
            dom.advPanel.style.display = open ? 'none' : 'block';
            dom.advToggle.setAttribute('aria-expanded', String(!open));
        });

        // Advanced inputs (courses + altitude)
        dom.inCourses.addEventListener('input', function () {
            dom.slCourses.value = dom.inCourses.value;
            calculate(false);
        });
        dom.slCourses.addEventListener('input', function () {
            dom.inCourses.value = dom.slCourses.value;
            calculate(false);
        });
        dom.inAltitude.addEventListener('input', function () {
            dom.slAltitude.value = dom.inAltitude.value;
            calculate(false);
        });
        dom.slAltitude.addEventListener('input', function () {
            dom.inAltitude.value = dom.slAltitude.value;
            calculate(false);
        });

        // Add to Cart button
        dom.productCardATC.addEventListener('click', addToCart);

        // Product card size chip clicks (event delegation)
        dom.productCardSizes.addEventListener('click', function (e) {
            var chip = e.target.closest('.sd-size-chip');
            if (chip && chip.dataset.variantId && !chip.disabled) {
                selectVariant(chip.dataset.variantId, chip);
            }
        });

        // Share button + popup
        dom.shareBtn.addEventListener('click', toggleSharePopup);
        dom.shareClose.addEventListener('click', toggleSharePopup);
        dom.sharePopup.addEventListener('click', function (e) {
            var opt = e.target.closest('.sd-share-option');
            if (opt && opt.dataset.platform) handleShare(opt.dataset.platform);
        });
        document.addEventListener('click', function (e) {
            if (dom.sharePopup.style.display !== 'none' &&
                !dom.sharePopup.contains(e.target) &&
                e.target !== dom.shareBtn &&
                !dom.shareBtn.contains(e.target)) {
                dom.sharePopup.style.display = 'none';
            }
        });

        // Expert contact buttons (event delegation on share-wrap)
        var shareWrap = dom.shareBtn.parentElement;
        shareWrap.addEventListener('click', function (e) {
            var btn = e.target.closest('.sd-expert-btn');
            if (btn && btn.dataset.expert) handleExpertContact(btn.dataset.expert);
        });

        // Size buttons (event delegation)
        dom.sizeButtons.addEventListener('click', function (e) {
            var btn = e.target.closest('.sd-size-btn');
            if (btn && btn.dataset.size) selectSize(parseInt(btn.dataset.size, 10));
        });
        dom.sizeButtons.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                var btn = e.target.closest('.sd-size-btn');
                if (btn && btn.dataset.size) { e.preventDefault(); selectSize(parseInt(btn.dataset.size, 10)); }
            }
        });
    }

    /* ================================================
       INIT
       ================================================ */
    function cacheDom() {
        dom.unitPill = document.getElementById('unit-pill');
        dom.btnWS = document.getElementById('btn-ws');
        dom.btnTandem = document.getElementById('btn-tandem');
        dom.btnReserve = document.getElementById('btn-reserve');

        dom.inExp = document.getElementById('in-exp');
        dom.slExp = document.getElementById('sl-exp');
        dom.inWeight = document.getElementById('in-weight');
        dom.slWeight = document.getElementById('sl-weight');
        dom.inGear = document.getElementById('in-gear');
        dom.slGear = document.getElementById('sl-gear');
        dom.inSize = document.getElementById('in-size');
        dom.slSize = document.getElementById('sl-size');
        dom.inWL = document.getElementById('in-wl');
        dom.slWL = document.getElementById('sl-wl');

        dom.resWL = document.getElementById('res-wl');
        dom.resBar = document.getElementById('res-bar');
        dom.resTotalW = document.getElementById('res-total-w');
        dom.resTierName = document.getElementById('res-tier-name');
        dom.resStatus = document.getElementById('res-status');
        dom.resDesc = document.getElementById('res-desc');
        dom.resCanopies = document.getElementById('res-canopies');
        dom.resSizesArea = document.getElementById('res-sizes-area');
        dom.selectedModelName = document.getElementById('selected-model-name');
        dom.sizeButtons = document.getElementById('size-buttons');
        dom.resAlert = document.getElementById('res-alert');

        dom.advToggle = document.getElementById('adv-toggle');
        dom.advPanel = document.getElementById('adv-panel');
        dom.inCourses = document.getElementById('in-courses');
        dom.slCourses = document.getElementById('sl-courses');
        dom.inAltitude = document.getElementById('in-altitude');
        dom.slAltitude = document.getElementById('sl-altitude');

        dom.productCardSection = document.getElementById('sd-product-card-section');
        dom.productCardATC = document.getElementById('product-card-atc');
        dom.productCardSizes = document.getElementById('product-card-sizes');

        dom.shareBtn = document.getElementById('sd-share-btn');
        dom.sharePopup = document.getElementById('sd-share-popup');
        dom.shareClose = document.getElementById('sd-share-close');
    }

    function init() {
        cacheDom();
        bindEvents();
        readURLState();
        calculate(false);
    }

    document.addEventListener('DOMContentLoaded', init);

})();
